<template>
  <v-container>
    <v-row>
      <!-- Action Buttons -->
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          color="warning"
          @click="handleCancel"
          :disabled="!hasChanges || isSaving"
          class="mr-5"
        >
          <v-icon>mdi-cancel</v-icon>
          <span class="d-none d-sm-block">Cancel</span>
        </v-btn>
        <v-btn color="primary" @click="saveChanges" :disabled="!hasChanges || isSaving">
          <v-icon>mdi-content-save</v-icon>
          <span class="d-none d-sm-block">Save</span>
        </v-btn>
      </v-col>

      <v-col cols="12">
        <v-expansion-panels>
          <!-- Header Settings -->
          <v-expansion-panel>
            <v-expansion-panel-title class="font-weight-bold">Header</v-expansion-panel-title>
            <v-expansion-panel-text>
              <!-- Header Preview -->
              <HeaderPreview :settings="globalSettings" />

              <v-tabs
                v-model="currentHeaderTab"
                color="primary"
                class="d-flex justify-space-around"
              >
                <v-tab value="mobile">
                  <v-icon start class="mr-0 mr-sm-2">mdi-cellphone</v-icon>
                  <span class="d-none d-sm-inline">Mobile</span>
                </v-tab>
                <v-tab value="desktop">
                  <v-icon start class="mr-0 mr-sm-2">mdi-monitor</v-icon>
                  <span class="d-none d-sm-inline">Desktop</span>
                </v-tab>
              </v-tabs>

              <v-window v-model="currentHeaderTab" :touch="false">
                <!-- Mobile Settings -->
                <v-window-item value="mobile">
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <UnitSettings
                          label="Model Name Font Size"
                          :modelValue="{
                            value: globalSettings?.header?.modelName?.mobile?.fontSize,
                            unit: globalSettings?.header?.modelName?.mobile?.fontSizeUnit,
                          }"
                          @update:modelValue="updateHeaderSettings('modelName', 'mobile', $event)"
                        />
                      </v-col>
                      <v-col cols="12">
                        <UnitSettings
                          label="Price Font Size"
                          :modelValue="{
                            value: globalSettings?.header?.price?.mobile?.fontSize,
                            unit: globalSettings?.header?.price?.mobile?.fontSizeUnit,
                          }"
                          @update:modelValue="updateHeaderSettings('price', 'mobile', $event)"
                        />
                      </v-col>
                      <v-col cols="12">
                        <UnitSettings
                          label="Trim Style Font Size"
                          :modelValue="{
                            value: globalSettings?.header?.trimStyle?.mobile?.fontSize,
                            unit: globalSettings?.header?.trimStyle?.mobile?.fontSizeUnit,
                          }"
                          @update:modelValue="updateHeaderSettings('trimStyle', 'mobile', $event)"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-window-item>
                <!-- Desktop Settings -->
                <v-window-item value="desktop">
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <UnitSettings
                          label="Model Name Font Size"
                          :modelValue="{
                            value: globalSettings?.header?.modelName?.desktop?.fontSize,
                            unit: globalSettings?.header?.modelName?.desktop?.fontSizeUnit,
                          }"
                          @update:modelValue="updateHeaderSettings('modelName', 'desktop', $event)"
                        />
                      </v-col>
                      <v-col cols="12">
                        <UnitSettings
                          label="Price Font Size"
                          :modelValue="{
                            value: globalSettings?.header?.price?.desktop?.fontSize,
                            unit: globalSettings?.header?.price?.desktop?.fontSizeUnit,
                          }"
                          @update:modelValue="updateHeaderSettings('price', 'desktop', $event)"
                        />
                      </v-col>
                      <v-col cols="12">
                        <UnitSettings
                          label="Trim Style Font Size"
                          :modelValue="{
                            value: globalSettings?.header?.trimStyle?.desktop?.fontSize,
                            unit: globalSettings?.header?.trimStyle?.desktop?.fontSizeUnit,
                          }"
                          @update:modelValue="updateHeaderSettings('trimStyle', 'desktop', $event)"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-window-item>
              </v-window>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Primary CTA Settings -->
          <v-expansion-panel v-if="globalSettings">
            <v-expansion-panel-title class="font-weight-bold">Primary CTA</v-expansion-panel-title>
            <v-expansion-panel-text>
              <CTAPreview :settings="globalSettings.primaryCTA" />
              <CTASettings v-model="globalSettings.primaryCTA" />
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Secondary CTA Settings -->
          <v-expansion-panel v-if="globalSettings">
            <v-expansion-panel-title class="font-weight-bold"
              >Secondary CTA</v-expansion-panel-title
            >
            <v-expansion-panel-text>
              <v-expansion-panel-text>
                <CTAPreview :settings="globalSettings.secondaryCTA" />
                <CTASettings v-model="globalSettings.secondaryCTA" />
              </v-expansion-panel-text>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Trim Card Setting -->
          <v-expansion-panel>
            <v-expansion-panel-title class="font-weight-bold">Trim Card</v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-container>
                <v-row v-if="globalSettings">
                  <v-col cols="12" class="d-flex justify-center">
                    <TrimCardPreview :settings="globalSettings" />
                  </v-col>
                  <v-col cols="12">
                    <UnitSettings
                      label="Card Width"
                      :modelValue="{
                        value: globalSettings?.trimCard.width,
                        unit: globalSettings?.trimCard.widthUnit,
                      }"
                      @update:modelValue="updateCardSettings('width', $event)"
                    />
                  </v-col>
                  <v-col cols="12">
                    <UnitSettings
                      label="Card Border Radius"
                      :modelValue="{
                        value: globalSettings?.trimCard.borderRadius,
                        unit: globalSettings?.trimCard.borderRadiusUnit,
                      }"
                      @update:modelValue="updateCardSettings('borderRadius', $event)"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <!-- Confirm Discard Dialog -->
    <v-dialog v-model="showConfirmDialog" max-width="500px">
      <v-card>
        <v-card-title>Unsaved Changes</v-card-title>
        <v-card-text>
          You have unsaved changes. Are you sure you want to discard them?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="showConfirmDialog = false">
            Keep Editing
          </v-btn>
          <v-btn color="error" @click="confirmDiscard"> Discard Changes </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>

  <!-- Snackbar -->
  <v-snackbar v-model="showSnackbar" :timeout="2000" :color="snackbarColor">
    {{ snackbarText }}

    <template #actions>
      <v-btn icon="mdi-close" @click="showSnackbar = false"></v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { CTASettings, UnitSettings } from '@jazel/jazel-vuetify'
import type { IControlUnitValues } from '@jazel/jazel-vuetify'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { getDefaultGlobalSettings } from '@/stores/utils'
import type { IGlobalSettings, IHeaderSettings } from '@/types'
import HeaderPreview from '@/components/HeaderPreview.vue'
import CTAPreview from '@/components/CTAPreview.vue'
import TrimCardPreview from '@/components/TrimCardPreview.vue'

const currentHeaderTab = ref('desktop')

const accountSettings = useAccountSettingsStore()
const productSettings = useProductSettingsStore()
const settings = computed(() => productSettings.settings)
const globalSettings = ref<IGlobalSettings | undefined>()

const showSnackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const isSaving = computed(() => productSettings.saving)
const showConfirmDialog = ref(false)

const pendingAction = ref<'cancel' | null>(null)

const hasChanges = computed(() => {
  return JSON.stringify(globalSettings.value) !== JSON.stringify(productSettings.settings?.globalSettings)
})

// Handle cancel button click event
const handleCancel = () => {
  if (hasChanges.value) {
    pendingAction.value = 'cancel'
    showConfirmDialog.value = true
  }
}

// Confirm discard changes
const confirmDiscard = () => {
  showConfirmDialog.value = false

  // Always revert changes first
  revertSettingsChanges()

  pendingAction.value = null
}

// Revert changes
const revertSettingsChanges = () => {
  globalSettings.value = JSON.parse(JSON.stringify(productSettings.settings?.globalSettings))
}

// Save settings
const saveChanges = () => {
  if (!hasChanges || !globalSettings.value || !settings.value) return

  const newSettings = { ...settings.value, globalSettings: globalSettings.value }

  productSettings
    .saveSettings(newSettings)
    .then(() => {
      showSnackbar.value = true
      snackbarText.value = 'Global settings saved successfully'
      snackbarColor.value = 'success'
    })
    .catch(() => {
      showSnackbar.value = true
      snackbarText.value = 'Failed to save global settings'
      snackbarColor.value = 'error'
    })
}

type IViewport = 'mobile' | 'desktop'

const updateHeaderSettings = (
  key: keyof IHeaderSettings,
  viewport: IViewport,
  value: IControlUnitValues,
) => {
  if (!globalSettings.value) return

  const updatedHeaderSettings = {
    ...globalSettings.value.header,
    [key]: {
      ...globalSettings.value.header[key],
      [viewport]: {
        fontSize: value.value,
        fontSizeUnit: value.unit,
      },
    },
  }

  globalSettings.value = {
    ...globalSettings.value,
    header: updatedHeaderSettings,
  }
}

const updateCardSettings = (key: string, value: IControlUnitValues) => {
  if (!globalSettings.value) return

  const updatedTrimCardSettings = {
    ...globalSettings.value.trimCard,
    [key]: value.value,
    [`${key}Unit`]: value.unit,
  }

  globalSettings.value = {
    ...globalSettings.value,
    trimCard: updatedTrimCardSettings,
  }
}

watch(
  () => productSettings.settings?.globalSettings,
  (newValue) => {
    globalSettings.value = JSON.parse(JSON.stringify(newValue))
  },
)

onMounted(() => {
  if (!globalSettings.value) {
    globalSettings.value =
      JSON.parse(JSON.stringify(productSettings.settings?.globalSettings)) ??
      getDefaultGlobalSettings(accountSettings)
  }
})
</script>

<style scoped>
.v-select .v-field .v-field__input > input {
  background: none;
  border: none;
  box-shadow: none;
}

:deep(.v-field__input) {
  background: none;
  border: none;
}

:deep(.v-field--variant-outlined .v-field__input) {
  padding: 12px 16px;
}

:deep(.v-field--variant-filled .v-field__input) {
  box-shadow: none;
  line-height: 1.625em;
  padding: 24px 16px 4px 16px;
}

@media (max-width: 425px) {
  :deep(.v-expansion-panel-text__wrapper) {
    padding-left: 10px;
    padding-right: 10px;
  }

  :deep(.v-expansion-panel-text__wrapper > .v-container) {
    padding: 0;
  }

  :deep(.v-card-text) {
    padding-left: 5px;
    padding-right: 5px;
  }
}
</style>
